import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Title, usePermissions } from 'react-admin';
import {
  Alert,
  Box,
  Chip,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { menuLabel } from '../admin/menuConfig';
import { FeaturesLoading, useFeatureFlags, useFeatureState } from '../admin/featureFlags/FeatureGate';
import { slideFor, useSlidesManifest, type SlidesManifest } from '../guide/slides';
import { MANUAL_CHAPTERS } from './content';
import {
  MANUAL_FEATURE,
  chapterForRole,
  isChapterKey,
  searchManual,
  visibleChapter,
} from './manualRules';
import { MANUAL_SLIDES_KEY, shotIndex } from './shots';
import type { ManualBlock, ManualChapter, ManualChapterKey, ManualImageRef, ManualSection } from './types';

/**
 * Instrukcja obsługi (decyzja użytkownika 28.10.2026, przełącznik
 * `userManual`, S-28.10-02) — rozdziały według działów i ról, na start
 * rozdział roli zalogowanego, wyszukiwarka po tekście.
 *
 * Treść jest w plikach (`manual/content/*.ts`), ekran ją tylko rysuje:
 * bloki z warunkiem pokazują się, gdy funkcja jest włączona dla konta
 * (`manualRules.ts`, te same reguły co w PDF). Zrzuty to slajdy nagrywarki
 * przewodników (`/guides/…`) — brak nagrania = instrukcja bez obrazów.
 */

export const FEATURE_OFF_NOTE = 'Instrukcja obsługi jest wyłączona w wersji aplikacji, z której korzystasz.';

/** Adres obrazu albo `null`, gdy nagrania nie ma. */
export function imageUrl(manifest: SlidesManifest | null, ref: ManualImageRef): string | null {
  if ('shot' in ref) {
    const i = shotIndex(ref.shot);
    return i < 0 ? null : slideFor(manifest, MANUAL_SLIDES_KEY, i)?.url ?? null;
  }
  return slideFor(manifest, ref.guide, ref.step - 1)?.url ?? null;
}

const TONE: Record<'system' | 'human' | 'warning', { severity: 'info' | 'success' | 'warning'; label: string }> = {
  system: { severity: 'info', label: 'Robi system' },
  human: { severity: 'success', label: 'Robi człowiek' },
  warning: { severity: 'warning', label: 'Uwaga' },
};

const Block = ({ block, manifest }: { block: ManualBlock; manifest: SlidesManifest | null }) => {
  switch (block.kind) {
    case 'text':
      return <Typography variant="body2" sx={{ mb: 1.5 }}>{block.text}</Typography>;
    case 'list':
      return (
        <Box sx={{ mb: 1.5 }}>
          {block.title && <Typography variant="subtitle2">{block.title}</Typography>}
          <Box component="ul" sx={{ my: 0.5, pl: 3 }}>
            {block.items.map((item) => (
              <Typography component="li" variant="body2" key={item}>{item}</Typography>
            ))}
          </Box>
        </Box>
      );
    case 'task':
      return (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }} data-testid="manual-task">
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{block.title}</Typography>
          {block.intro && <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>{block.intro}</Typography>}
          <Box component="ol" sx={{ my: 0.5, pl: 3 }}>
            {block.steps.map((step, i) => (
              <Typography component="li" variant="body2" key={i}>{step}</Typography>
            ))}
          </Box>
          {block.outcome && <Typography variant="body2" sx={{ color: 'text.secondary' }}>{block.outcome}</Typography>}
        </Paper>
      );
    case 'terms':
      return (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{block.title}</Typography>
          <Box component="dl" sx={{ m: 0 }}>
            {block.items.map((i) => (
              <Box key={i.term} sx={{ display: 'flex', gap: 1, py: 0.25, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                <Box component="dt" sx={{ minWidth: { md: 220 }, fontWeight: 600, fontSize: 14 }}>{i.term}</Box>
                <Box component="dd" sx={{ m: 0, fontSize: 14 }}>{i.meaning}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      );
    case 'note': {
      const tone = TONE[block.tone];
      return (
        <Alert severity={tone.severity} sx={{ mb: 1.5 }} data-testid={`manual-note-${block.tone}`}>
          <strong>{tone.label}:</strong> {block.text}
        </Alert>
      );
    }
    case 'image': {
      const url = imageUrl(manifest, block.image);
      if (!url) return null;
      return (
        <Box component="figure" sx={{ m: 0, mb: 1.5 }} data-testid="manual-image">
          <a href={url} target="_blank" rel="noreferrer">
            <Box component="img" src={url} alt={block.caption} sx={{ maxWidth: '100%', border: 1, borderColor: 'divider', borderRadius: 1 }} />
          </a>
          <Typography component="figcaption" variant="caption" sx={{ color: 'text.secondary' }}>{block.caption}</Typography>
        </Box>
      );
    }
    default:
      return null;
  }
};

const Section = ({ chapter, section, manifest }: { chapter: ManualChapterKey; section: ManualSection; manifest: SlidesManifest | null }) => (
  <Box component="section" id={`manual-${chapter}-${section.id}`} data-testid={`manual-section-${section.id}`} sx={{ mb: 3, scrollMarginTop: 72 }}>
    <Typography variant="h6" sx={{ mb: 1 }}>{section.title}</Typography>
    {section.blocks.map((b, i) => (
      <Block key={i} block={b} manifest={manifest} />
    ))}
  </Box>
);

const scrollToSection = (chapter: ManualChapterKey, sectionId: string) => {
  // Po zmianie rozdziału sekcja pojawia się w następnej klatce.
  window.setTimeout(() => {
    document.getElementById(`manual-${chapter}-${sectionId}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, 50);
};

/** Rozdział z adresu (`#/manual?chapter=trade`) — odnośnik z innego miejsca. */
function chapterFromAddress(): ManualChapterKey | null {
  const query = window.location.hash.split('?')[1] ?? '';
  const value = new URLSearchParams(query).get('chapter');
  return isChapterKey(value) ? value : null;
}

const ManualContent = () => {
  const { permissions } = usePermissions<string>();
  const flags = useFeatureFlags();
  const { manifest } = useSlidesManifest();
  const chapters: ManualChapter[] = useMemo(
    () => MANUAL_CHAPTERS.map((c) => visibleChapter(c, (f) => flags?.flags[f] === true)),
    [flags],
  );
  const [chosen, setChosen] = useState<ManualChapterKey | null>(chapterFromAddress);
  const [query, setQuery] = useState('');
  // Na start rozdział roli — dopiero gdy rola jest znana, i tylko do pierwszego wyboru.
  const current: ManualChapterKey = chosen ?? chapterForRole(permissions);
  useEffect(() => {
    if (chosen === null && permissions) setChosen(chapterForRole(permissions));
  }, [chosen, permissions]);
  const chapter = chapters.find((c) => c.key === current) ?? chapters[0];
  const hits = useMemo(() => searchManual(chapters, query), [chapters, query]);
  const searching = query.trim().length >= 2;

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' }, py: 2 }} data-testid="manual-screen">
      <Paper variant="outlined" sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0, position: { md: 'sticky' }, top: { md: 64 } }}>
        <Box sx={{ p: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Szukaj w instrukcji"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            slotProps={{
              htmlInput: { 'data-testid': 'manual-search', 'aria-label': 'Szukaj w instrukcji' },
              input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> },
            }}
          />
        </Box>
        <List dense data-testid="manual-chapters" aria-label="Rozdziały instrukcji">
          {chapters.map((c) => (
            <ListItemButton
              key={c.key}
              selected={!searching && c.key === chapter.key}
              onClick={() => {
                setChosen(c.key);
                setQuery('');
              }}
              data-testid={`manual-chapter-${c.key}`}
            >
              <ListItemText primary={c.title} />
              {c.key === chapterForRole(permissions) && <Chip size="small" label="Twoja rola" sx={{ ml: 1 }} />}
            </ListItemButton>
          ))}
        </List>
      </Paper>
      <Box sx={{ flex: 1, minWidth: 0, maxWidth: 980 }}>
        {searching ? (
          <Box data-testid="manual-search-results">
            <Typography variant="h5" sx={{ mb: 1 }}>
              Wyniki dla „{query.trim()}”
            </Typography>
            {hits.length === 0 ? (
              <Typography variant="body2" data-testid="manual-search-empty">
                Nic nie znaleziono. Spróbuj innego słowa albo krótszej frazy.
              </Typography>
            ) : (
              <List>
                {hits.map((h) => (
                  <ListItemButton
                    key={`${h.chapter}-${h.sectionId}`}
                    data-testid={`manual-hit-${h.chapter}-${h.sectionId}`}
                    onClick={() => {
                      setChosen(h.chapter);
                      setQuery('');
                      scrollToSection(h.chapter, h.sectionId);
                    }}
                    sx={{ alignItems: 'flex-start' }}
                  >
                    <ListItemText primary={`${h.chapterTitle} › ${h.sectionTitle}`} secondary={h.snippet} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        ) : (
          <Box data-testid={`manual-chapter-content-${chapter.key}`}>
            <Typography variant="h4" sx={{ mb: 0.5 }}>{chapter.title}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>{chapter.audience}</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>{chapter.intro}</Typography>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 3 }} data-testid="manual-toc">
              <Typography variant="subtitle2">W tym rozdziale</Typography>
              <Stack component="ul" sx={{ m: 0, pl: 3 }}>
                {chapter.sections.map((s) => (
                  <Typography component="li" variant="body2" key={s.id}>
                    <Box
                      component="a"
                      href={`#manual-${chapter.key}-${s.id}`}
                      onClick={(e: MouseEvent) => {
                        e.preventDefault();
                        scrollToSection(chapter.key, s.id);
                      }}
                      sx={{ color: 'primary.main' }}
                    >
                      {s.title}
                    </Box>
                  </Typography>
                ))}
              </Stack>
            </Paper>
            {chapter.sections.map((s) => (
              <Section key={s.id} chapter={chapter.key} section={s} manifest={manifest} />
            ))}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Instrukcja opisuje funkcje włączone dla Twojego konta. Zrzuty ekranów pochodzą z danych pokazowych.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/** Ekran „Instrukcja” — za przełącznikiem; wyłączony: komunikat (pozycji w menu wtedy nie ma). */
export const ManualScreen = () => {
  const on = useFeatureState(MANUAL_FEATURE);
  return (
    <Box>
      <Title title={menuLabel('manual')} />
      {on === undefined ? (
        <FeaturesLoading />
      ) : on ? (
        <ManualContent />
      ) : (
        <Alert severity="info" sx={{ mt: 2 }} data-testid="manual-off">
          {FEATURE_OFF_NOTE}
        </Alert>
      )}
    </Box>
  );
};
